import { notification } from 'antd/lib'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { OrderStatusAcceptedLocationState } from 'sections/LKSection/modules/OrderModule/pages/OrderItemPage/OrderItemStatusView/Statuses/Accepted/OrderStatusAccepted'
import { Href } from 'components/ui/Href/Href'
import { Button, Flex, Modal, Text } from 'components/uiKit'
import { getActivateOrderRoute, getOrderItemRoute } from 'utils/helpers/routes/getRoutes'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { useResize } from 'utils/hooks/useResize'

type OpenELockModalProps = {
  orderId: number
  isModalVisible: boolean
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>
}

export const OpenELockModal = observer(({ orderId, isModalVisible, setIsModalVisible }: OpenELockModalProps) => {
  const { eLockUiStore: uiStore } = useStore()

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isTablet } = useResize()

  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)

  const isActive = uiStore.isActive

  const openButtonHandler = () => {
    setIsConfirmModalVisible(true)
    setIsModalVisible(false)
  }

  const okButtonHandler = async () => {
    if (uiStore.isOnline) {
      let result = false

      if (!isActive) {
        setLoading(true)
        result = await uiStore.activateELock()
        setLoading(false)
      }

      if (result || isActive) {
        navigate(getActivateOrderRoute(orderId))
      }
    } else {
      notification.warning({ message: t(getKey('Необходимо подключиться')) })
    }
  }

  const cancelButtonHandler = async () => {
    if (uiStore.isOnline) {
      navigate(getOrderItemRoute(orderId), {
        state: {
          fromActivateELockPage: true,
        } as OrderStatusAcceptedLocationState,
      })
    } else {
      notification.warning({ message: t(getKey('Необходимо подключиться')) })
    }
  }

  const getKey = (key: string) => `${OrderKeys.eLockModule}.${key}`

  return (
    <>
      <Modal
        title={t(getKey('Данные для подключения'))}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        maskClosable={false}
        footer={null}
      >
        <Flex vertical gap={16}>
          <Flex vertical>
            <Text strong>{t(getSharedKey('Внимание'))}!</Text>
            <Text>{t(getKey('На данном шаге необходимо'))}</Text>
          </Flex>

          <Text>{t(getKey('После подключения к Wifi'))}</Text>

          <Text>{t(getKey('После успешного открытия'))}</Text>

          <Flex vertical>
            <Text>
              <Text strong>{t(getKey('Название точки доступа'))}</Text>{' '}
              <Text copyable>{uiStore.eLockConnectInfo?.ssid}</Text>
            </Text>

            <Text>
              <Text strong>{t(getKey('Пароль к точке доступа'))}</Text>{' '}
              <Text copyable>{uiStore.eLockConnectInfo?.psk}</Text>
            </Text>
          </Flex>

          <Flex vertical gap={8}>
            {uiStore.eLockConnectInfo && (
              <Href href={uiStore.eLockConnectInfo.linkToOpen} linkType="search" target="_blank">
                <Button onClick={openButtonHandler} style={{ width: '100%' }}>
                  {t(getKey('Открыть замок'))}
                </Button>
              </Href>
            )}
          </Flex>
        </Flex>
      </Modal>

      <Modal
        title={t(getKey('Ваш замок открылся'))}
        open={isConfirmModalVisible}
        centered
        maskClosable={false}
        width={600}
        onCancel={() => setIsConfirmModalVisible(false)}
        footer={
          <Flex gap={8} justify="flex-end" vertical={isTablet}>
            <Button type="default" onClick={cancelButtonHandler}>
              {t(getKey('Нет, перейти к активации'))}
            </Button>
            <Button onClick={okButtonHandler} loading={loading}>
              {isActive ? t(getKey('Да, активировать ПВЗ')) : t(getKey('Да, активировать замок'))}
            </Button>
          </Flex>
        }
      >
        <Flex vertical gap={8}>
          <Text>
            {t(getKey('Если щелчок произошел'))}{' '}
            {isActive ? `"${t(getKey('Да, активировать ПВЗ'))}"` : `"${t(getKey('Да, активировать замок'))}"`}
          </Text>
          <Text>{t(getKey('Если вы не услышали'))}</Text>
        </Flex>
      </Modal>
    </>
  )
})
